import { ICellRendererParams } from 'ag-grid-community';
import { merge } from 'lodash-es';
import * as yup from 'yup';
import { IStatusRendererParams, StatusRenderer } from '../components/AgTable/cell-renderers/StatusRenderer';
import { createCompanySearchResultFields } from '../data-models/company-search-result.data-model';
import { CompanySearchResponseSchema, createCompanySearchResponse } from './CompanySearchResponse.schema';

export const CompanyMappingAction = {
  create: 'create',
  skip: 'skip',
} as const;
export type CompanyMappingAction = (typeof CompanyMappingAction)[keyof typeof CompanyMappingAction];

export const CompanyMappingStatus = {
  init: 'init',
  preMapped: 'pre-mapped',
  mapped: 'mapped',
  skipped: 'skipped',
} as const;
export type CompanyMappingStatus = (typeof CompanyMappingStatus)[keyof typeof CompanyMappingStatus];

export const CompanyMappingStatusUI = {
  ...CompanyMappingStatus,
  modified: 'modified',
} as const;
export type CompanyMappingStatusUI = (typeof CompanyMappingStatusUI)[keyof typeof CompanyMappingStatusUI];

export const MappingStatusToLabel: Record<CompanyMappingStatusUI, string> = {
  [CompanyMappingStatus.init]: 'Unmapped',
  [CompanyMappingStatus.preMapped]: 'Needs Review',
  [CompanyMappingStatus.mapped]: 'Mapped',
  [CompanyMappingStatus.skipped]: 'Skipped',
  [CompanyMappingStatusUI.modified]: 'Modified',
};

export function companyMappingPayloadFields() {
  return {
    id: yup.string().required(),
    companyName: yup.string(),
    companySite: yup.string().nullable(),
    companyId: yup.number().nullable(),
    providerId: yup.string().required(),
    provider: yup.string().required(),
    providerCompanyName: yup.string().required(),
    action: yup.string().oneOf(Object.values(CompanyMappingAction)).required(),
  };
}

export function companyMappingResponseFields() {
  const { action, companyName, ...rest } = companyMappingPayloadFields();
  return {
    ...rest,
    companyName: yup.string().nullable(),
    status: yup.string().oneOf(Object.values(CompanyMappingStatus)).required(),
  };
}

export function companyMappingPayloadSchema() {
  const { companyName, ...rest } = companyMappingPayloadFields();
  return yup.object({
    ...rest,
    companyName: yup.string().when('action', {
      is: (action: CompanyMappingAction) => action === CompanyMappingAction.skip,
      then: (schema) => schema.nullable(),
      otherwise: (schema) => schema.required(),
    }),
  });
}

export function companyMappingVMFields() {
  const { status, ...rest } = companyMappingResponseFields();
  return {
    company: yup.object(CompanySearchResponseSchema.base()),
    modified: yup.boolean(),
    status: yup
      .string()
      .oneOf(Object.values(CompanyMappingStatusUI))
      .required()
      .gridMeta({
        cellRenderer: (params: ICellRendererParams<CompanyMappingResponse>) => {
          if (!params.value) {
            return null;
          }
          return (
            <StatusRenderer
              status={
                params.value ? MappingStatusToStatusEnum[params.value as CompanyMappingStatusUI] : 'pending'
              }
              value={params.valueFormatted ?? params.value}
            />
          );
        },
      }),
    ...rest,
  };
}

export function companyMappingVMSchema() {
  return yup.object(companyMappingVMFields());
}

export type CompanyMappingVM = yup.InferType<ReturnType<typeof companyMappingVMSchema>>;

export type CompanyMappingPayload = yup.InferType<ReturnType<typeof companyMappingPayloadSchema>>;

export function companyMappingResponseSchema() {
  return yup.object(companyMappingResponseFields());
}
export type CompanyMappingResponse = yup.InferType<ReturnType<typeof companyMappingResponseSchema>>;

export function createCompanyMappingResponse(
  overrides?: Partial<CompanyMappingResponse>
): CompanyMappingResponse {
  return {
    id: '1',
    companyName: '',
    companySite: '',
    status: CompanyMappingStatus.init,
    providerId: '1',
    provider: '',
    providerCompanyName: '',
    ...overrides,
  };
}
export function createCompanyMappingVM(overrides?: Partial<CompanyMappingVM>): CompanyMappingVM {
  return merge(
    {},
    createCompanyMappingResponse(),
    {
      company: createCompanySearchResponse({
        id: '',
        fields: createCompanySearchResultFields({
          name: '',
          website: '',
          type: '',
        }),
      }),
    },
    { modified: false },
    overrides
  );
}

export function companyMappingResponseToVM(response: CompanyMappingResponse): CompanyMappingVM {
  const company = createCompanySearchResponse({
    id: response.companyId != null ? String(response.companyId) : '',
    fields: createCompanySearchResultFields({
      name: response.companyName ?? '',
      website: response.companySite ?? '',
      type: '',
    }),
  });
  return { company, ...response };
}

const terminalMappingStatuses = new Set<string>([CompanyMappingStatus.mapped, CompanyMappingStatus.skipped]);

// The real Foresight company id backing a row, or null when there's none yet: an empty id
// (unresolved / AI-proposed company not in Foresight) or a non-Foresight search hit (e.g. a
// Crunchbase `vendor-<uuid>` id, which saving turns into a create). The "Will create" chip and
// the save payload both derive create-vs-map from this, so they can't disagree.
export function foresightCompanyId(company?: { id?: string | number | null }): number | null {
  if (company?.id == null || company.id === '') {
    return null;
  }
  const id = Number(company.id);
  return Number.isInteger(id) && id > 0 ? id : null;
}

// A row will create a new company on save when it has a name but no Foresight company id yet.
// Terminal rows (mapped/skipped) are excluded unless actively re-edited — skip nulls the id but
// keeps the name, which would otherwise show a false chip on historical skipped rows.
export function willCreateMapping(data?: CompanyMappingVM | null): boolean {
  if (!data?.company?.fields?.name || foresightCompanyId(data.company) != null) {
    return false;
  }
  return Boolean(data.modified) || !terminalMappingStatuses.has(data.status);
}

const MappingStatusToStatusEnum: Record<CompanyMappingStatusUI, IStatusRendererParams['status']> = {
  [CompanyMappingStatus.init]: 'warning',
  [CompanyMappingStatus.preMapped]: 'maybe',
  [CompanyMappingStatus.mapped]: 'success',
  [CompanyMappingStatus.skipped]: 'hidden',
  [CompanyMappingStatusUI.modified]: 'modified',
};
